import { formatIsoDate, maskDocumentNumber, translatePropToLabel } from "~/helpers";
import { BaseModel, BaseItemData, ItemType } from './base.model';
import type { MergeableReportItem } from "~/types/global";

/**
 * A simplified, normalized representation of a mergeable report for the UI.
 */
export interface NormalizedMergeableReport extends BaseItemData {
  reportType: string;
  searchArgs: Record<string, string>;
  userReportsId: string;
  type: ItemType.REPORT;
  relatedDocuments: string[];
  relatedDocumentsPlain: string[];
}

/**
 * Wrapper around raw MergeableReportItem, exposing easy-to-use getters
 */
export class MergeableReportModel implements BaseModel<NormalizedMergeableReport> {
  readonly type = ItemType.REPORT;

  constructor(public readonly raw: MergeableReportItem) { }

  get id(): string {
    return this.raw.user_reports_id;
  }

  get name(): string {
    return this.raw.report_name || `Relatório ${this.reportType}`;
  }

  get reportType(): string {
    return this.raw.report_type || "unknown";
  }

  get searchArgs(): Record<string, string> {
    return this.raw.report_search_args || {};
  }

  get userReportsId(): string {
    return this.raw.user_reports_id;
  }

  get createdAt(): string {
    return this.modifiedAt;
  }

  get modifiedAt(): string {
    return formatIsoDate(this.raw.modified_at);
  }

  get formattedSearchArgs(): Array<{ key: string; values: string[]; label: string }> {
    return Object.entries(this.searchArgs)
      .filter(([key]) => key !== 'related_documents')
      .map(([key, value]) => {
        const cleanKey = key.replace(/_[12]$/, '');
        const values = Array.isArray(value) ? value : [value];
        // redes_sociais is a URL/handle; placa/chassi/veiculo are alphanumeric,
        // not CPF/CNPJ/telefone — maskDocumentNumber would mangle both. Mirrors
        // the fix in report.model.ts's searchItems.
        const skipMask = cleanKey === "redes_sociais" || cleanKey === "placa" || cleanKey === "chassi" || cleanKey === "veiculo";
        return {
          key,
          values: values.map(val => skipMask ? String(val) : maskDocumentNumber(val, cleanKey)),
          label: translatePropToLabel(cleanKey).toUpperCase(),
        };
      });
  }

  get canBeMerged(): boolean {
    return Boolean(this.userReportsId && this.relatedDocumentsPlain.length > 0);
  }

  get relatedDocuments(): string[] {
    return this.raw.related_documents || [];
  }

  get relatedDocumentsPlain(): string[] {
    if (!this.raw.related_documents_encrypted) return [];
    return Array.isArray(this.raw.related_documents_encrypted)
      ? this.raw.related_documents_encrypted as string[]
      : [];
  }

  toJSON(): NormalizedMergeableReport {
    return {
      id: this.id,
      name: this.name,
      reportType: this.reportType,
      searchArgs: this.searchArgs,
      userReportsId: this.userReportsId,
      createdAt: this.createdAt,
      modifiedAt: this.modifiedAt,
      type: ItemType.REPORT,
      relatedDocuments: this.relatedDocuments,
      relatedDocumentsPlain: this.relatedDocumentsPlain,
    };
  }
}
